import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import type SurveyCompletedJournalEvent from "parog-web/models/survey-completed-journal-event";
import tWithDefault from "parog-web/utils/t-with-default";

//---- Automatically generated, don't update this part ----

type VisibleOnlyByStringEnum = "medics" | "patients" | "all";

export default class SurveyRequestedJournalEvent extends JournalEvent {
  static override type = "survey-requested-journal-event";

  @attr("string") declare formId: string;

  @attr("string") declare visibleOnlyBy: VisibleOnlyByStringEnum;

  @attr("date") declare expireAt: Date;

  @belongsTo("survey-completed-journal-event", {
    async: false,
    inverse: "surveyRequestedJournalEvent"
  })
  declare surveyCompletedJournalEvent: SurveyCompletedJournalEvent;

  static override validations(intl: IntlService) {
    return {
      formId: validatePresence({
        presence: true,
        description: intl.t(
          "validations.survey-requested-journal-event.form-id"
        )
      }),
      visibleOnlyBy: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.survey-requested-journal-event.visible-only-by"
          )
        }),
        validateInclusion({
          list: ["medics", "patients", "all"],
          description: intl.t(
            "validations.survey-requested-journal-event.visible-only-by"
          )
        })
      ],
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.survey-requested-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "surveyRequestedJournalEvents"
  })
  declare patient: Patient;

  get isNotCompleted(): boolean {
    // using == instead of === on purpose here
    // the previous implementation was using import { none } from '@ember/object/computed';
    // and @none checks both null and undefined
    // https://api.emberjs.com/ember/release/functions/@ember%2Fobject%2Fcomputed/none
    return this.surveyCompletedJournalEvent?.id == null;
  }

  get isCompleted(): boolean {
    return !this.isNotCompleted;
  }

  get isVisibleOnlyByPatient(): boolean {
    return this.visibleOnlyBy === "patients" || this.visibleOnlyBy === "all";
  }

  get isVisibleOnlyByMedics(): boolean {
    return this.visibleOnlyBy === "medics" || this.visibleOnlyBy === "all";
  }

  get isExpired(): boolean {
    if (this.expireAt) {
      const currentDate = new Date();
      return this.expireAt < currentDate;
    }
    return false;
  }

  /**
   * @param authenticatedAs authenticated user
   * @returns `true` if it is visible by the given authenticated
   * target
   */
  isVisibleBy(authenticatedAs: string): boolean {
    if (authenticatedAs === "medic") {
      return this.isVisibleOnlyByMedics;
    } else if (authenticatedAs === "patient") {
      return this.isVisibleOnlyByPatient;
    }
    return false;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "survey-requested-journal-event": SurveyRequestedJournalEvent;
  }
}
